/**
 * Features Component - HI-SI Landing Page
 * HISIP-016: Seven-stage pipeline highlights
 */

import { motion } from 'framer-motion';
import { 
  PenTool, 
  Eye, 
  Grid, 
  Film, 
  Compass, 
  Image, 
  Layers,
  Brain,
  Zap,
  Target,
  Bot,
  Music,
  Upload
} from 'lucide-react';
import { cn } from '@/utils/cn';

const PIPELINE_STAGES = [
  {
    icon: Bot,
    name: 'Agent Orchestrator',
    persona: 'The Agent',
    description: 'Guides tools, selects models, and orchestrates each stage for outcomes',
    color: 'violet',
    gradient: 'from-violet-500/20 to-violet-600/10'
  },
  {
    icon: PenTool,
    name: 'Script Generation',
    persona: 'The Writer',
    description: 'AI crafts compelling narratives with perfect pacing and engagement',
    color: 'purple',
    gradient: 'from-purple-500/20 to-purple-600/10'
  },
  {
    icon: Eye,
    name: 'Vision Development',
    persona: 'The Architect',
    description: 'Strategic creative direction that aligns visuals with narrative goals',
    color: 'cyan',
    gradient: 'from-cyan-500/20 to-cyan-600/10'
  },
  {
    icon: Grid,
    name: 'Storyboard Planning',
    persona: 'The Architect',
    description: 'Detailed visual blueprints for seamless shot composition',
    color: 'emerald',
    gradient: 'from-emerald-500/20 to-emerald-600/10'
  },
  {
    icon: Film,
    name: 'Shot Composition',
    persona: 'The Operator',
    description: 'Cinematic camera angles and movements for professional quality',
    color: 'amber',
    gradient: 'from-amber-500/20 to-amber-600/10'
  },
  {
    icon: Compass,
    name: 'Continuity Management',
    persona: 'The Operator',
    description: 'Ensures consistent flow and visual coherence throughout',
    color: 'red',
    gradient: 'from-red-500/20 to-red-600/10'
  },
  {
    icon: Image,
    name: 'Frame Generation',
    persona: 'The Operator',
    description: 'High-quality imagery that matches your creative vision perfectly',
    color: 'indigo',
    gradient: 'from-indigo-500/20 to-indigo-600/10'
  },
  {
    icon: Layers,
    name: 'Video Assembly',
    persona: 'The Operator',
    description: 'Final production with seamless transitions and professional polish',
    color: 'teal',
    gradient: 'from-teal-500/20 to-teal-600/10'
  },
  {
    icon: Layers,
    name: 'Assembly',
    persona: 'The Operator',
    description: 'Combine and sequence outputs for delivery',
    color: 'slate',
    gradient: 'from-slate-500/20 to-slate-600/10'
  },
  {
    icon: Music,
    name: 'Music',
    persona: 'The Operator',
    description: 'Compose and fit soundtrack automatically',
    color: 'blue',
    gradient: 'from-blue-500/20 to-blue-600/10'
  },
  {
    icon: Upload,
    name: 'Publishing',
    persona: 'The Operator',
    description: 'Export and deliver on every channel',
    color: 'violet',
    gradient: 'from-violet-500/20 to-violet-600/10'
  }
];

const CORE_FEATURES = [
  {
    icon: Brain,
    title: 'AI-Powered Personas',
    description: 'Specialized AI agents handle different creative roles - Writers, Architects, and Operators work together seamlessly'
  },
  {
    icon: Target,
    title: 'Iterative Refinement',
    description: 'Each stage iterates until quality thresholds are met, ensuring professional-grade output every time'
  },
  {
    icon: Zap,
    title: 'Lightning Speed',
    description: 'Complete video production in minutes, not hours - from concept to final render automatically'
  }
];

export default function Features() {
  return (
    <section className="relative py-20 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-900/10 to-black" />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(168,85,247,0.3)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.3)_1px,transparent_1px)] bg-[size:100px_100px]" />
        </div>
        
        {/* Floating elements */}
        <motion.div
          className="absolute top-32 left-10 w-24 h-24 rounded-full bg-cyan-500/5 blur-2xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-32 right-10 w-32 h-32 rounded-full bg-purple-500/5 blur-2xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-light text-white mb-6">
            <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Eleven‑Stage Intelligence
            </span>
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
            Each stage works in concert—guided by <span className="text-cyan-300 font-medium">The Agent</span>—to turn ideas into on‑brand videos with speed and consistency.
          </p>
        </motion.div>

        {/* Pipeline Stages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-20">
          {PIPELINE_STAGES.map((stage, index) => (
            <motion.div
              key={stage.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="group"
            >
              <div className={cn(
                "h-full p-6 rounded-xl backdrop-blur-xl border transition-all duration-300",
                "bg-black/30 border-white/10",
                "hover:bg-black/50 hover:border-purple-500/30",
                "hover:shadow-xl hover:shadow-purple-500/10"
              )}>
                {/* Stage Icon */}
                <div className="mb-4">
                  <div className={cn(
                    "w-12 h-12 rounded-xl flex items-center justify-center",
                    "bg-gradient-to-br", stage.gradient,
                    "group-hover:scale-110 transition-transform duration-300"
                  )}>
                    <stage.icon className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Stage Info */}
                <div className="mb-3">
                  <h3 className="text-lg font-medium text-white mb-1 group-hover:text-purple-300 transition-colors">
                    {stage.name}
                  </h3>
                  <div className="text-xs text-cyan-400 font-mono uppercase tracking-wider">
                    {stage.persona}
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-white/70 leading-relaxed">
                  {stage.description}
                </p>

                {/* Stage Number */}
                <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <span className="text-xs font-mono text-purple-400">{index + 1}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Core Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {CORE_FEATURES.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="text-center group"
            >
              {/* Feature Icon */}
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/20 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-8 h-8 text-purple-400 group-hover:text-cyan-400 transition-colors" />
                </div>
              </div>

              {/* Feature Content */}
              <h3 className="text-xl font-medium text-white mb-4 group-hover:text-purple-300 transition-colors">
                {feature.title}
              </h3>
              <p className="text-white/70 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Quality Promise */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20">
            <Target className="w-5 h-5 text-purple-400" />
            <span className="text-white font-medium">
              Quality threshold: 85% minimum • Unlimited iterations
            </span>
          </div>
        </motion.div>

      </div>
    </section>
  );
}